import React, { useState } from "react";
import {
  Typography,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BulkUpdateDialog from "../../../../components/BulkUpdateDialog";
import {
  AccordionDetailsStyled,
  AccordionStyled,
  AccordionSummaryStyled,
} from "app/pages/components/mui/Accordions/StyledAccordion/StyleAccordion";

const TestsAccordion = ({
  expanded,
  onChange,
  problemTests,
  handleAddTest,
  handleDeleteTest,
  handleTestChange,
  variableTypes,
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  const validateTests = (parsedTests) => {
    parsedTests.forEach((test, index) => {
      if (!test.problemInputs || !test.problemOutputs) {
        throw new Error(
          `Test at index ${index} is missing problemInputs or problemOutputs`
        );
      }
    });
  };

  const handleBulkUpdateTests = (parsedTests) => {
    const startIndex = problemTests.length;
    parsedTests.forEach((test, index) => {
      const newIndex = startIndex + index;
      handleAddTest({ stopPropagation: () => {} });
      test.problemInputs.forEach((input, inputIndex) => {
        if (inputIndex >= problemTests[newIndex]?.problemInputs?.length) {
          handleTestChange(newIndex, "problemInputs", null, "add");
        }
        handleTestChange(
          newIndex,
          "problemInputs",
          inputIndex,
          "variableName",
          input.variableName
        );
        handleTestChange(
          newIndex,
          "problemInputs",
          inputIndex,
          "value",
          input.value
        );
        handleTestChange(
          newIndex,
          "problemInputs",
          inputIndex,
          "variableType",
          input.variableType
        );
      });
      if (test.problemOutputs.length > 0) {
        handleTestChange(
          newIndex,
          "problemOutputs",
          0,
          "variableName",
          test.problemOutputs[0].variableName
        );
        handleTestChange(
          newIndex,
          "problemOutputs",
          0,
          "value",
          test.problemOutputs[0].value
        );
        handleTestChange(
          newIndex,
          "problemOutputs",
          0,
          "variableType",
          test.problemOutputs[0].variableType
        );
      }
    });
  };

  return (
    <>
      <AccordionStyled expanded={expanded} onChange={onChange}>
        <AccordionSummaryStyled
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="subtitle1" sx={{ alignSelf: "center" }}>
            Tests
          </Typography>
          {expanded && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDialog(true);
                }}
              >
                Bulk Update
              </Button>
              <IconButton
                color="#475259"
                aria-label="Add Test"
                onClick={handleAddTest}
                disabled={!expanded}
              >
                <AddIcon />
              </IconButton>
            </Box>
          )}
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {problemTests.map((test, testIndex) => (
            <Box
              key={testIndex}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 2,
                p: 1,
                border: "1px solid #ccc",
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="subtitle2" sx={{ alignSelf: "center" }}>
                  Inputs
                </Typography>
                <IconButton
                  onClick={() =>
                    handleTestChange(testIndex, "problemInputs", null, "add")
                  }
                >
                  <AddIcon />
                </IconButton>
              </Box>
              {test.problemInputs.map((input, inputIndex) => (
                <Box
                  key={inputIndex}
                  sx={{ display: "flex", gap: 2, alignItems: "center" }}
                >
                  <TextField
                    label="Variable Name"
                    placeholder="e.g., nums"
                    value={input.variableName}
                    onChange={(e) =>
                      handleTestChange(
                        testIndex,
                        "problemInputs",
                        inputIndex,
                        "variableName",
                        e.target.value
                      )
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value"
                    placeholder="e.g., [1, 2, 3, 4]"
                    value={input.value}
                    onChange={(e) =>
                      handleTestChange(
                        testIndex,
                        "problemInputs",
                        inputIndex,
                        "value",
                        e.target.value
                      )
                    }
                    sx={{ flex: 2 }}
                  />
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={input.variableType || ""}
                      onChange={(e) =>
                        handleTestChange(
                          testIndex,
                          "problemInputs",
                          inputIndex,
                          "variableType",
                          e.target.value
                        )
                      }
                      displayEmpty
                      renderValue={(value) => value || "Select Variable Type"}
                    >
                      <MenuItem value="" disabled>
                        Select Variable Type
                      </MenuItem>
                      {variableTypes?.map((type) => (
                        <MenuItem key={type.key} value={type.key}>
                          {type.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    color="error"
                    onClick={() =>
                      handleTestChange(
                        testIndex,
                        "problemInputs",
                        inputIndex,
                        "delete"
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Typography variant="subtitle2">Output</Typography>
              {test.problemOutputs.length > 0 && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <TextField
                    label="Variable Name"
                    placeholder="e.g., result"
                    value={test.problemOutputs[0].variableName}
                    onChange={(e) =>
                      handleTestChange(
                        testIndex,
                        "problemOutputs",
                        0,
                        "variableName",
                        e.target.value
                      )
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value"
                    placeholder="e.g., [1, 2]"
                    value={test.problemOutputs[0].value}
                    onChange={(e) =>
                      handleTestChange(
                        testIndex,
                        "problemOutputs",
                        0,
                        "value",
                        e.target.value
                      )
                    }
                    sx={{ flex: 2 }}
                  />
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={test.problemOutputs[0].variableType || ""}
                      onChange={(e) =>
                        handleTestChange(
                          testIndex,
                          "problemOutputs",
                          0,
                          "variableType",
                          e.target.value
                        )
                      }
                      displayEmpty
                      renderValue={(value) => value || "Select Variable Type"}
                    >
                      <MenuItem value="" disabled>
                        Select Variable Type
                      </MenuItem>
                      {variableTypes?.map((type) => (
                        <MenuItem key={type.key} value={type.key}>
                          {type.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  color="error"
                  aria-label="Delete Test"
                  onClick={() => handleDeleteTest(testIndex)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </AccordionDetailsStyled>
      </AccordionStyled>

      <BulkUpdateDialog
        title="Bulk Update Tests"
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onBulkUpdate={handleBulkUpdateTests}
        validateInput={validateTests}
        successMessage="Tests added successfully!"
        helperText="Enter an array of test cases in JSON format (e.g., [{ 'problemInputs': [{ 'variableName': 'nums', 'value': '[1, 2, 3, 4]', 'variableType': 'ARRAY' },{'variableName': 'sum','value': '5','variableType': 'INTEGER'}], 'problemOutputs': [{ 'variableName': 'result', 'value': '[1, 4]', 'variableType': 'ARRAY' }] }])"
      />
    </>
  );
};

export default TestsAccordion;
